/**
 * Fingerprint id cache/fetch, shared between the prefetch trigger
 * (giftcard-home.js, as soon as a token is entered) and the consumer
 * (google-pay-provisioning.js, just before card-id is fetched).
 *
 * Stores fingerprint_id in sessionStorage to reuse across multiple Google
 * Pay provisioning attempts in the same session, and dedupes concurrent
 * callers so a prefetch and a later consumer never fire two HTTP requests.
 */

import { collectWebFingerprint } from './fingerprint';

const STORAGE_KEY = 'giftstarr_fingerprint_id';
let inflight = null;

export const FingerprintManager = {
    STORAGE_KEY,

    get: () => {
        try {
            return sessionStorage.getItem(STORAGE_KEY);
        } catch {
            return null;
        }
    },

    set: (fingerprintId) => {
        try {
            sessionStorage.setItem(STORAGE_KEY, fingerprintId);
        } catch {
            // Ignore storage errors
        }
    },
};

/**
 * Returns a cached fingerprint_id, or fetches and caches a new one.
 * Safe to call from multiple places (e.g. an early prefetch and a later
 * consumer) — concurrent calls share a single in-flight request.
 *
 * @param {object} api - instance returned by API() (comps/api.js)
 * @returns {Promise<string>} fingerprintId
 */
export async function ensureFingerprintId(api) {
    const cached = FingerprintManager.get();
    if (cached) return cached;

    if (inflight) return inflight;

    inflight = (async () => {
        try {
            const fpData = collectWebFingerprint();
            const fpResponse = await api.createFingerprint(fpData);
            if (fpResponse.error) throw new Error(fpResponse.error);
            const fingerprintId = fpResponse.fingerprint_id;
            FingerprintManager.set(fingerprintId);
            return fingerprintId;
        } finally {
            inflight = null;
        }
    })();

    return inflight;
}
